import React, { useEffect, useRef } from 'react'
import event1 from '../assets/event_Images/event1.jpeg';
import event2 from '../assets/event_Images/event2.jpeg';
import invite from '../assets/event_Images/invite.png';
import Navbar from "../components/layout/Navbar3";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Events = () => {
  const heroRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroImgRef = useRef(null);
  const speakersTitleRef = useRef(null);
  const speaker1Ref = useRef(null);
  const speaker2Ref = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        heroTitleRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power4.out", delay: 0.2 }
      );

      gsap.fromTo(
        heroImgRef.current,
        { y: 60, opacity: 0, scale: 0.96 },
        { y: 0, opacity: 1, scale: 1, duration: 1.2, ease: "power3.out", delay: 0.4 }
      );

      gsap.fromTo(
        speakersTitleRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: speakersTitleRef.current,
            start: "top 85%",
          },
        }
      );

      gsap.fromTo(
        [speaker1Ref.current, speaker2Ref.current],
        { y: 50, opacity: 0, rotateY: 8, transformPerspective: 1000 },
        {
          y: 0,
          opacity: 1,
          rotateY: 0,
          duration: 0.9,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: speaker1Ref.current,
            start: "top 85%",
          },
        }
      );
    });

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;

      if (blob1Ref.current) blob1Ref.current.style.transform = `translate(${x * 30}px, ${y * 30}px)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate(${x * -40}px, ${y * 20}px)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate(${x * 20}px, ${y * -30}px)`;
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="bg-slate-950 min-h-screen text-white overflow-hidden selection:bg-blue-500/30 relative">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 transition-transform duration-100 ease-out" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 transition-transform duration-100 ease-out" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 transition-transform duration-100 ease-out" />

      <Navbar />

      <div className="max-w-5xl mx-auto px-5 py-8 relative z-10">
        <section ref={heroRef} className="pt-20 pb-12 text-center relative">
          <h1 ref={heroTitleRef} className="font-inter text-4xl md:text-6xl lg:text-7xl font-black tracking-tight text-gradient-mask mb-4 opacity-0">
            Event Details
          </h1>
          <p className="font-playfair italic text-xl text-gray-500 mb-12">
            Experience the future of technology
          </p>

          {/* Main Event Card Image — Sized down to max-w-2xl */}
          <div ref={heroImgRef} className="relative group opacity-0 mx-auto max-w-2xl">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
            <div className="relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 backdrop-blur-sm shadow-2xl shadow-black/40">
              <img src={invite} alt="Main Event Card" className="w-full h-auto object-cover" />
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-950/60 to-transparent pointer-events-none" />
            </div>
          </div>
        </section>

        <div className="relative py-12 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.5)] animate-pulse" />
        </div>

        <section className="pb-24">
          <div className="text-center mb-14">
            <h2 ref={speakersTitleRef} className="font-inter text-3xl md:text-5xl font-bold text-gradient-mask tracking-tight mb-4 opacity-0">
              Speakers / Highlights
            </h2>
            <p className="font-playfair italic text-lg text-gray-500">
              Industry leaders and visionaries
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            <div ref={speaker1Ref} className="group relative w-full sm:w-[calc(50%-12px)] max-w-md opacity-0">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition-opacity duration-500" />
              <div className="relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 backdrop-blur-sm shadow-2xl shadow-black/40 hover:border-white/20 transition-all duration-500">
                <div className="relative overflow-hidden">
                  <img src={event1} alt="Speaker 1" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-60" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="w-8 h-[2px] bg-blue-500 rounded-full mb-3" />
                  <h3 className="text-lg font-semibold text-white">Keynote Speaker</h3>
                  <p className="text-sm text-gray-400 mt-1">Industry Expert</p>
                </div>
              </div>
            </div>

            <div ref={speaker2Ref} className="group relative w-full sm:w-[calc(50%-12px)] max-w-md opacity-0">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/20 to-rose-600/20 rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition-opacity duration-500" />
              <div className="relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 backdrop-blur-sm shadow-2xl shadow-black/40 hover:border-white/20 transition-all duration-500">
                <div className="relative overflow-hidden">
                  <img src={event2} alt="Speaker 2" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-60" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="w-8 h-[2px] bg-purple-500 rounded-full mb-3" />
                  <h3 className="text-lg font-semibold text-white">Special Guest</h3>
                  <p className="text-sm text-gray-400 mt-1">Thought Leader</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Events;